/**
 * Week prize slots 1–4 — images and copy keys must stay in sync with app/prizes/page.tsx
 */
export type WeekPrizeSlot = 1 | 2 | 3 | 4;

const WEEK_IMAGES: Record<WeekPrizeSlot, string> = {
  1: "https://betcenter-gaming.s3.eu-west-1.amazonaws.com/prizes/prizes-week-1.png",
  2: "https://betcenter-gaming.s3.eu-west-1.amazonaws.com/prizes/prizes-week-2.png",
  3: "https://betcenter-gaming.s3.eu-west-1.amazonaws.com/prizes/prizes-week-3.png",
  4: "https://betcenter-gaming.s3.eu-west-1.amazonaws.com/prizes/prizes-week-4.png",
};

/** i18n key under `prizes.*` for the prize title */
const TITLE_KEYS: Record<WeekPrizeSlot, string> = {
  1: "prizes.ps5_slim",
  2: "prizes.ipad_air_m3",
  3: "prizes.samsung_tab_s10",
  4: "prizes.sony_wh1000xm6",
};

/** 10th scheduled week = PS5 (slot 1); then cycles 1→2→3→4. Matches calendar week 10 when there are no gaps. */
const ANCHOR_WEEK = 10;

/** Competition week 1 had no weekly prize; from week 2 onward the weekly prize applies. */
export function hasWeeklyPrizeForCompetitionWeek(weekId: number): boolean {
  return weekId !== 1;
}

/** Sorted unique `weekId`s from the competition schedule (same order as the week switcher). */
export function getSortedScheduledWeekIds(
  weeks: { weekId: number }[] | null | undefined,
): number[] {
  if (!weeks?.length) return [];
  const ids = weeks
    .map((w) => w.weekId)
    .filter((n): n is number => typeof n === "number" && Number.isFinite(n));
  return [...new Set(ids)].sort((a, b) => a - b);
}

/** 1-based index of `weekId` in the schedule, or null if not found. */
export function getScheduledWeekSequenceIndex(
  weekId: number,
  scheduledWeekIds: number[],
): number | null {
  const i = scheduledWeekIds.indexOf(weekId);
  if (i === -1) return null;
  return i + 1;
}

function slotFromAnchorWeek(anchorInput: number): WeekPrizeSlot {
  const w = Number.isFinite(anchorInput) && anchorInput >= 1 ? anchorInput : 1;
  const normalized = ((w - ANCHOR_WEEK) % 4 + 4) % 4;
  return ((normalized + 1) as WeekPrizeSlot);
}

/**
 * Uses the 1-based position in {@link getSortedScheduledWeekIds} so missing week numbers (e.g. no week 17)
 * do not shift the prize cycle. Falls back to calendar `weekId` when the schedule is empty or unknown.
 */
export function getWeekPrizeSlotForWeek(
  weekId: number,
  weeks: { weekId: number }[] | null | undefined,
): WeekPrizeSlot {
  const scheduled = getSortedScheduledWeekIds(weeks);
  if (scheduled.length > 0) {
    const seq = getScheduledWeekSequenceIndex(weekId, scheduled);
    if (seq != null) return slotFromAnchorWeek(seq);
  }
  return slotFromAnchorWeek(weekId);
}

/**
 * Maps calendar competition week number to slot 1–4 (same pool as /prizes).
 * Prefer {@link getWeekPrizeSlotForWeek} when the schedule is known.
 */
export function getWeekPrizeSlot(weekId: number): WeekPrizeSlot {
  return slotFromAnchorWeek(weekId);
}

export function getWeekPrizeConfig(slot: WeekPrizeSlot) {
  return {
    slot,
    imageSrc: WEEK_IMAGES[slot],
    titleKey: TITLE_KEYS[slot],
  };
}
